import { useMemo, useState } from "react";
import {
  Award,
  Building2,
  Coins,
  Crown,
  Factory,
  HardHat,
  Home,
  Landmark,
  Maximize2,
  Ruler,
  Users,
  type LucideIcon,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Loader } from "@/components/ui/Loader";
import { formatCurrency, formatNumber } from "@/utils/format";
import { cn } from "@/utils/cn";
import { ClienteStat } from "../chartUtils";

/** Roxo principal (referência). */
const PURPLE = "#7B5BE4";
const PURPLE_SOFT = "rgba(123, 91, 228, 0.14)";
const TRACK = "#F3F4F6";

const ROW_ICONS: LucideIcon[] = [Building2, Factory, HardHat, Home, Landmark, Ruler];

/** Valor à direita: R$ 12,478k (milhares, pt-BR). */
function formatValorKLabel(value: number): string {
  const k = value / 1000;
  if (!Number.isFinite(k) || k === 0) return formatCurrency(0);
  if (Math.abs(k) >= 1000) return formatCurrency(value, true);
  const decimals = Math.abs(k - Math.round(k)) < 1e-6 ? 0 : 3;
  return `R$ ${formatNumber(k, decimals)}k`;
}

/** Ticks 0 … eixo máximo “bonito” (ex.: 0k, 3.500k, 7.000k…). */
function buildAxisTicks(maxValue: number, targetSteps = 5): number[] {
  if (!Number.isFinite(maxValue) || maxValue <= 0) return [0, 1];
  const maxK = maxValue / 1000;
  const raw = maxK / Math.max(1, targetSteps - 1);
  const pow10 = 10 ** Math.floor(Math.log10(Math.max(raw, 1e-9)));
  const stepK =
    [1, 2, 2.5, 5, 10].map((m) => m * pow10).find((s) => s >= raw) ?? 10 * pow10;
  const endK = Math.ceil(maxK / stepK) * stepK;
  const ticks: number[] = [];
  for (let k = 0; k <= endK + 1e-9; k += stepK) {
    ticks.push(k * 1000);
  }
  if (ticks.length < 2) ticks.push(stepK * 1000);
  return ticks;
}

function formatAxisK(value: number): string {
  return `${formatNumber(value / 1000, 0)}k`;
}

function RankBadge({ rank }: { rank: number }) {
  const cls =
    "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold shadow-sm sm:h-8 sm:w-8 sm:text-[11px]";
  if (rank === 1) {
    return (
      <div
        className={cn(
          cls,
          "text-amber-950 bg-gradient-to-br from-amber-200 via-amber-400 to-amber-600 ring-2 ring-amber-100/90",
        )}
      >
        1
      </div>
    );
  }
  if (rank === 2) {
    return (
      <div
        className={cn(
          cls,
          "text-slate-800 bg-gradient-to-br from-slate-100 via-slate-300 to-slate-400 ring-2 ring-slate-200/90",
        )}
      >
        2
      </div>
    );
  }
  if (rank === 3) {
    return (
      <div
        className={cn(
          cls,
          "text-orange-950 bg-gradient-to-br from-orange-300 via-amber-700 to-orange-900 ring-2 ring-orange-200/80",
        )}
      >
        3
      </div>
    );
  }
  return (
    <span
      className="flex h-7 w-7 shrink-0 items-center justify-center text-xs font-bold tabular-nums sm:h-8 sm:w-8 sm:text-sm"
      style={{ color: PURPLE }}
    >
      {rank}
    </span>
  );
}

/* ─── Modal ─────────────────────────────────────────────────────────────── */
function ModalContent({
  cliente,
  detailKind,
}: {
  cliente: ClienteStat;
  detailKind: "document" | "client";
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl bg-purple-50 p-3 text-center">
          <p className="text-xs font-semibold uppercase tracking-wide text-purple-600">Total</p>
          <p className="mt-0.5 text-lg font-bold text-slate-800">{formatCurrency(cliente.valor)}</p>
        </div>
        <div className="rounded-xl bg-emerald-50 p-3 text-center">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600">Recebido</p>
          <p className="mt-0.5 text-lg font-bold text-slate-800">{formatCurrency(cliente.pago)}</p>
        </div>
        <div className="rounded-xl bg-amber-50 p-3 text-center">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">Em Aberto</p>
          <p className="mt-0.5 text-lg font-bold text-slate-800">{formatCurrency(cliente.aberto)}</p>
        </div>
      </div>

      <div className="max-h-80 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="py-2 pr-3 text-left font-semibold text-slate-500">
                {detailKind === "client" ? "Cliente" : "Tipo Doc."}
              </th>
              <th className="py-2 pr-3 text-left font-semibold text-slate-500">Vencimento</th>
              <th className="py-2 pr-3 text-right font-semibold text-slate-500">Valor</th>
              <th className="py-2 text-center font-semibold text-slate-500">Status</th>
            </tr>
          </thead>
          <tbody>
            {cliente.items.map((item, i) => (
              <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/60">
                <td className="py-1.5 pr-3 text-slate-500 max-w-[160px] truncate">
                  {detailKind === "client"
                    ? item.clientName || "—"
                    : item.documentType || "—"}
                </td>
                <td className="py-1.5 pr-3 text-slate-500">{item.dueDate || "—"}</td>
                <td className="py-1.5 pr-3 text-right font-medium text-slate-800">
                  {formatCurrency(item.originalAmount)}
                </td>
                <td className="py-1.5 text-center">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${item.isPaid ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
                  >
                    {item.isPaid ? "Pago" : "Aberto"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {cliente.items.length === 0 && (
          <p className="py-4 text-center text-sm text-slate-400">Sem registros</p>
        )}
      </div>
    </div>
  );
}

interface Props {
  data: ClienteStat[];
  loading: boolean;
  title?: string;
  subtitle?: string;
  /** Ação para abrir a visualização completa do ranking. */
  onExpand?: () => void;
  /** Exibe a quantidade de casas vendidas imediatamente antes do valor. */
  showQuantity?: boolean;
  /** Coluna de detalhe no modal: tipo de documento (clientes) ou nome do cliente (corretores). */
  detailKind?: "document" | "client";
  /** Faz o ranking ocupar a altura disponível na visualização expandida. */
  expanded?: boolean;
}

/** Colunas: posição | ícone | nome | trilha+barra | valor (alinhado ao eixo). */
const ROW_GRID =
  "grid w-full min-w-0 grid-cols-[2rem_2rem_minmax(7.5rem,10rem)_1fr_5rem] items-center gap-x-2 sm:grid-cols-[2rem_2rem_minmax(8rem,11rem)_1fr_5.5rem] sm:gap-x-2.5";
const ROW_GRID_WITH_QUANTITY =
  "grid w-full min-w-0 grid-cols-[2rem_2rem_minmax(7.5rem,10rem)_1fr_4.5rem_5rem] items-center gap-x-2 sm:grid-cols-[2rem_2rem_minmax(8rem,11rem)_1fr_5rem_5.5rem] sm:gap-x-2.5";

export function RankingClientes({
  data,
  loading,
  title = "Ranking de Clientes",
  subtitle = "Top 10 por valor de vendas no período",
  onExpand,
  showQuantity = false,
  detailKind = "document",
  expanded = false,
}: Props) {
  const [selected, setSelected] = useState<ClienteStat | null>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const rowGrid = showQuantity ? ROW_GRID_WITH_QUANTITY : ROW_GRID;

  const maxVal = useMemo(() => Math.max(...data.map((d) => d.valor), 1), [data]);
  const axisTicks = useMemo(() => buildAxisTicks(maxVal, 5), [maxVal]);
  const axisMax = axisTicks[axisTicks.length - 1] ?? maxVal;

  const empty = !loading && data.length === 0;

  return (
    <>
      <div
        className={cn(
          "flex flex-col gap-3 rounded-2xl border border-slate-200/80 bg-white p-3 shadow-card sm:p-4",
          expanded && "h-full min-h-[58dvh]",
        )}
      >
        {/* Cabeçalho — coroa + título + medalha + expandir */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 items-start gap-2">
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full shadow-sm"
              style={{ background: PURPLE_SOFT, color: PURPLE }}
            >
              <Crown className="h-4 w-4" strokeWidth={2} />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-1.5">
                <h3 className="text-sm font-bold tracking-tight text-slate-900 sm:text-[15px]">{title}</h3>
                <Award className="h-3.5 w-3.5 shrink-0 text-violet-500" strokeWidth={2} />
              </div>
              <p className="mt-0.5 text-[11px] text-slate-500">{subtitle}</p>
            </div>
          </div>
          {onExpand && (
            <button
              type="button"
              onClick={onExpand}
              className="shrink-0 rounded-lg border border-slate-200/90 bg-slate-50 p-1.5 text-slate-500 transition-colors hover:border-violet-200 hover:bg-violet-50 hover:text-violet-600"
              title="Ver ranking completo"
              aria-label="Ver ranking completo"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className={cn("min-w-0", expanded && "flex min-h-0 flex-1 flex-col")}>
          {loading ? (
            <div className="flex min-h-[100px] items-center justify-center py-4">
              <Loader size="md" label="Carregando dados..." />
            </div>
          ) : empty ? (
            <div className="flex min-h-[72px] flex-col items-center justify-center gap-2 py-4 text-slate-400">
              <Users className="h-10 w-10 opacity-40" />
              <p className="text-sm font-medium">Sem dados para exibir.</p>
            </div>
          ) : (
            <div className={cn("flex flex-col gap-0.5", expanded && "min-h-0 flex-1")}>
              <div
                className={cn(
                  "relative flex max-h-[200px] flex-col gap-1.5 overflow-y-auto overscroll-y-contain pt-0.5 pr-0.5 sm:max-h-[220px]",
                  expanded && "max-h-none flex-1 sm:max-h-none",
                )}
              >
                  {data.map((row, idx) => {
                    const rank = idx + 1;
                    const pct = axisMax > 0 ? (row.valor / axisMax) * 100 : 0;
                    const Icon = ROW_ICONS[idx % ROW_ICONS.length];
                    const active = hoverIdx === idx;

                    return (
                      <button
                        key={`${row.name}-${idx}`}
                        type="button"
                        className={cn(
                          rowGrid,
                          "rounded-lg py-1 pl-0.5 pr-0.5 text-left transition-colors",
                          "hover:bg-slate-50/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-violet-300/80",
                        )}
                        onClick={() => setSelected(row)}
                        onMouseEnter={() => setHoverIdx(idx)}
                        onMouseLeave={() => setHoverIdx(null)}
                      >
                        <RankBadge rank={rank} />
                        <div
                          className="flex h-7 w-7 shrink-0 items-center justify-center justify-self-center rounded-md text-white shadow-sm sm:h-8 sm:w-8"
                          style={{
                            background: active ? PURPLE : `${PURPLE}f0`,
                          }}
                        >
                          <Icon className="h-3.5 w-3.5 opacity-95 sm:h-4 sm:w-4" strokeWidth={2} />
                        </div>
                        <div
                          className="min-w-0 truncate text-xs font-medium text-slate-700 sm:text-[13px]"
                          title={row.name}
                        >
                          {row.name.trim()}
                        </div>
                        <div className="relative min-w-0 self-center">
                          <div className="pointer-events-none absolute inset-0 z-0">
                            {axisTicks.map((t, i) => {
                              if (t <= 0) return null;
                              return (
                                <div
                                  key={i}
                                  className="absolute bottom-0 top-0 border-l border-dashed border-slate-200/95"
                                  style={{ left: `${(t / axisMax) * 100}%` }}
                                />
                              );
                            })}
                          </div>
                          <div
                            className="relative z-[1] h-2 w-full overflow-hidden rounded-full"
                            style={{ backgroundColor: TRACK }}
                          >
                            <div
                              className="h-full rounded-full transition-[width,filter] duration-200 ease-out"
                              style={{
                                width: `${Math.min(100, Math.max(0, pct))}%`,
                                backgroundColor: PURPLE,
                                filter: active ? "brightness(1.06)" : undefined,
                              }}
                            />
                          </div>
                        </div>
                        {showQuantity && (
                          <div
                            className="text-right text-[10px] font-semibold tabular-nums text-slate-500 sm:text-xs"
                            title={`${row.quantidade} ${row.quantidade === 1 ? "casa vendida" : "casas vendidas"}`}
                          >
                            {row.quantidade} {row.quantidade === 1 ? "casa" : "casas"}
                          </div>
                        )}
                        <div className="text-right text-[10px] font-bold tabular-nums text-slate-800 sm:text-xs">
                          {formatValorKLabel(row.valor)}
                        </div>
                      </button>
                    );
                  })}
                </div>

              {/* Eixo + legenda */}
              <div className="mt-0.5 border-t border-slate-100 pt-1.5">
                <div className={cn(rowGrid, "pointer-events-none text-[9px] font-medium tabular-nums text-slate-400 sm:text-[10px]")}>
                  <div className="col-span-3" aria-hidden />
                  <div className="flex justify-between px-0.5">
                    {axisTicks.map((t) => (
                      <span key={t} className="tabular-nums">
                        {formatAxisK(t)}
                      </span>
                    ))}
                  </div>
                  <div aria-hidden className={cn("min-w-[1px]", showQuantity && "col-span-2")} />
                </div>
                <div className="mt-1 flex items-center justify-center gap-1 text-[11px] text-slate-500">
                  <Coins className="h-3 w-3 text-amber-500/90" strokeWidth={2} />
                  <span>Valor de vendas (R$)</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        size="lg"
        title={
          selected && (
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-50">
                <Users className="h-4 w-4" style={{ color: PURPLE }} />
              </div>
              <span className="max-w-[260px] truncate text-sm font-semibold text-slate-800">{selected.name}</span>
            </div>
          )
        }
      >
        {selected && <ModalContent cliente={selected} detailKind={detailKind} />}
      </Modal>
    </>
  );
}
